'use client';

import * as React from 'react';
import Image from 'next/image';
import { cn } from '@/lib/utils';

/** Same single bundled mark the rest of the app uses (see TenantLogo). */
const LOGO_SRC = '/images/icon2.png';

/**
 * The brand loader mark: the NSI logo sat inside a slightly-thick arc that
 * spins right on the logo's circular edge — so the ring reads as one piece with
 * the mark, not a separate spinner floating around it. A faint full-circle
 * track sits under the arc (the industry-standard determinate-looking spinner)
 * so it never looks "stuck". Honours reduced-motion by holding still.
 *
 * Pure design-system primitive: it references the bundled asset directly rather
 * than importing the branding feature, keeping shared/ free of feature imports.
 */
export function BrandSpinner({
  size = 88,
  label = 'Loading',
  className,
}: {
  /** Outer diameter in px — the logo fills it and the ring hugs its edge. */
  size?: number;
  label?: string;
  className?: string;
}) {
  // viewBox is a fixed 0–100 square, so stroke width is in those units. ~5 units
  // reads as a "slightly thick" ring across the sizes we use it at (64–112px).
  const STROKE = 5;
  // r chosen so the outer stroke edge lands ~on the box edge (= the logo's own
  // circular border), which is where the mark's ring sits in the artwork.
  const R = 50 - STROKE / 2;

  return (
    <div
      role="status"
      aria-label={label}
      className={cn('relative inline-flex shrink-0 items-center justify-center', className)}
      style={{ width: size, height: size }}
    >
      <Image
        src={LOGO_SRC}
        alt=""
        width={size}
        height={size}
        priority
        aria-hidden="true"
        className="object-contain"
        style={{ width: size, height: size }}
      />
      <svg
        viewBox="0 0 100 100"
        fill="none"
        aria-hidden="true"
        className="absolute inset-0 h-full w-full [animation-duration:0.9s] motion-safe:animate-spin"
      >
        {/* A faint primary tint acts as the track — kept in the blue family (not
            grey) so it never dulls the logo's own printed ring underneath. */}
        <circle cx="50" cy="50" r={R} stroke="var(--primary)" strokeWidth={STROKE} opacity={0.18} />
        {/* The moving arc, spinning right on the logo's circular edge. */}
        <circle
          cx="50"
          cy="50"
          r={R}
          stroke="var(--primary)"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={`${2 * Math.PI * R * 0.28} ${2 * Math.PI * R}`}
        />
      </svg>
      <span className="sr-only">{label}</span>
    </div>
  );
}
